import AppLayout from '@/layout/AppLayout';
import i18n from '@/i18n';
import LeftNavLayout from '@/layout/LeftNavLayout';
import { joinPrefix, mergeRouters } from '@/routes/util';
import { lazyNamedImport } from '@/utils/lazyNamedImport';
import { lazy } from 'react';
import ErrorBoundary from '../ErrorBoundary';

const BaseInfo = lazy(() => import('@/pages/clusters/pages/BaseInfo'));
const Condition = lazy(() => import('@/pages/clusters/pages/Condition'));
const Namespace = lazyNamedImport(() => import('@/pages/clusters/pages/Namespace'), 'Namespace');
const NamespaceDetail = lazyNamedImport(() => import('@/pages/clusters/pages/Namespace/Detail'), 'NamespaceDetail');
const DefaultContainerQuota = lazyNamedImport(() => import('@/pages/clusters/pages/Namespace/Detail/defaultContainerQuota'), 'DefaultContainerQuota');
const NamespaceQuota = lazyNamedImport(() => import('@/pages/clusters/pages/Namespace/Detail/namespaceQuota'), 'NamespaceQuota');
const Node = lazyNamedImport(() => import('@/pages/clusters/pages/Nodes'), 'Node');
const NodeDetails = lazyNamedImport(() => import('@/pages/clusters/pages/Nodes/Detail'), 'NodeDetails');
const EditLabel = lazyNamedImport(() => import('@/pages/clusters/pages/Nodes/Detail/editLabel'), 'EditLabel');
const ContainerGroup = lazyNamedImport(() => import('@/pages/clusters/pages/Pods'), 'ContainerGroup');
const ContainerDetail = lazyNamedImport(() => import('@/pages/clusters/pages/Pods/ContainerDetail'), 'ContainerDetail');
const PodsDetail = lazyNamedImport(() => import('@/pages/clusters/pages/Pods/Detail'), 'PodsDetail');
const CreateService = lazyNamedImport(() => import('@/pages/clusters/pages/Service/create/create-service'), 'CreateService');
const Service = lazyNamedImport(() => import('@/pages/clusters/pages/Service/service'), 'Service');
const loadServiceDetails = () => import('@/pages/clusters/pages/Service/details');
const ServiceContainerDetails = lazyNamedImport(loadServiceDetails, 'ServiceContainerDetails');
const ServiceDetailsContainer = lazyNamedImport(loadServiceDetails, 'ServiceDetailsContainer');
const ServicePodsDetails = lazyNamedImport(loadServiceDetails, 'ServicePodsDetails');
const Pv = lazyNamedImport(() => import('@/pages/clusters/pages/Storage/Pv'), 'Pv');
const Pvc = lazyNamedImport(() => import('@/pages/clusters/pages/Storage/Pvc'), 'Pvc');
const CreatePvc = lazyNamedImport(() => import('@/pages/clusters/pages/Storage/Pvc/components/Create'), 'CreatePvc');
const PvcDetail = lazyNamedImport(() => import('@/pages/clusters/pages/Storage/Pvc/Detail'), 'PvcDetail');
const PvDetail = lazyNamedImport(() => import('@/pages/clusters/pages/Storage/Pvc/pvDetail'), 'PvDetail');
const StorageClass = lazyNamedImport(() => import('@/pages/clusters/pages/Storage/StorageClass'), 'StorageClass');
const CreateStorageClass = lazyNamedImport(() => import('@/pages/clusters/pages/Storage/StorageClass/components/Create'), 'CreateStorageClass');
const StorageClassDetail = lazyNamedImport(() => import('@/pages/clusters/pages/Storage/StorageClass/Detail'), 'StorageClassDetail');
const t = i18n.t.bind(i18n);

/** ============================ 配置 ==================================== */
const Secret = lazyNamedImport(() => import('@/pages/clusters/pages/Configure/Secret'), 'Secret');
const CreateSecret = lazyNamedImport(() => import('@/pages/clusters/pages/Configure/Secret/components/Create'), 'CreateSecret');
const SecretDetail = lazyNamedImport(() => import('@/pages/clusters/pages/Configure/Secret/Detail'), 'SecretDetail');
// cspell:ignore Configmap
const Configmap = lazyNamedImport(() => import('@/pages/clusters/pages/Configure/Configmap'), 'Configmap');
const CreateConfigmap = lazyNamedImport(() => import('@/pages/clusters/pages/Configure/Configmap/components/Create'), 'CreateConfigmap');
const ConfigmapDetail = lazyNamedImport(() => import('@/pages/clusters/pages/Configure/Configmap/Detail'), 'ConfigmapDetail');
/** ============================ 工作负载 ==================================== */
const WorkloadRedirect = lazyNamedImport(() => import('@/pages/clusters/pages/Workload/workload-redirect'), 'WorkloadRedirect');
const Workload = lazyNamedImport(() => import('@/pages/clusters/pages/Workload/workload'), 'Workload');
const CreateWorkload = lazyNamedImport(() => import('@/pages/clusters/pages/Workload/create/create-workload'), 'CreateWorkload');
const loadWorkloadDetails = () => import('@/pages/clusters/pages/Workload/details');
const WorkloadDetailsContainer = lazyNamedImport(loadWorkloadDetails, 'WorkloadDetailsContainer');
const WorkloadContainerDetails = lazyNamedImport(loadWorkloadDetails, 'WorkloadContainerDetails');
const WorkloadPodsDetails = lazyNamedImport(loadWorkloadDetails, 'WorkloadPodsDetails');
const WorkloadServiceDetails = lazyNamedImport(loadWorkloadDetails, 'WorkloadServiceDetails');
const UpdateWorkload = lazyNamedImport(() => import('@/pages/clusters/pages/Workload/update'), 'UpdateWorkload');

// 集群信息
const clusterInfo = [
  {
    title: t('alert:alert-overview-pl'),
    path: '/base-info',
    element: <BaseInfo />,
  },
  {
    title: t('cluster:cluster-node-pl'),
    path: '/:type/nodes',
    element: <Node />,
  },
  {
    title: t('cluster:cluster-condition'),
    path: '/cluster-condition',
    element: <Condition />,
  },
  {
    title: t('auth-conf:get.node.details'),
    path: '/:type/nodes/:node',
    element: <NodeDetails />,
  },
  {
    title: t('cluster:edit-label'),
    path: '/:type/nodes/:node/edit-label',
    element: <EditLabel />,
  },
];

// 命名空间
const namespaces = [
  {
    title: 'namespaces',
    path: '/namespaces',
    element: <Namespace />,
  },
  {
    title: 'namespace-detail',
    path: '/namespaces/:namespace/detail',
    element: <NamespaceDetail />,
  },
  {
    title: 'edit-namespace-quota',
    path: '/namespaces/:namespace/edit-quota',
    element: <NamespaceQuota />,
  },
  {
    title: 'edit-defaultContainer-quota',
    path: '/namespaces/:namespace/edit-defaultContainer-quota',
    element: <DefaultContainerQuota />,
  },
];

// 工作负载
const workloads = [
  {
    title: 'workload-default',
    path: '/workload',
    element: <WorkloadRedirect />, // 或者做重定向到 deployment
  },
  {
    title: 'workload',
    path: '/workload/:workload',
    element: <Workload />,
  },
  {
    title: 'workload-create', // 创建
    path: '/workload/:workload/create',
    element: <CreateWorkload />,
  },
  {
    title: 'workload-detail', // 详情
    path: '/workload/:workload/namespace/:namespace/detail/:deployment',
    element: <WorkloadDetailsContainer />,
  },
  {
    title: 'workload-update', // 编辑
    path: '/workload/:workload/namespace/:namespace/update/:deployment',
    element: <UpdateWorkload />,
  },
  {
    title: 'workload-pods-detail',
    path: '/workload/:workload/namespace/:namespace/detail/:deployment/pods/:pods',
    element: <WorkloadPodsDetails />,
  },
  {
    title: 'workload-pod-detail',
    path: '/workload/:workload/namespace/:namespace/detail/:deployment/pods/:pods/:container',
    element: <WorkloadContainerDetails />,
  },
  {
    title: 'workload-service-detail',
    path: '/workload/:workload/namespace/:namespace/detail/:deployment/service/:service',
    element: <WorkloadServiceDetails />,
  },
];

// 容器组
const containerGroup = [
  {
    title: 'pods',
    path: '/pods',
    element: <ContainerGroup />,
  },
  {
    title: 'pods-detail',
    path: '/pods/:pods/namespace/:namespace/detail',
    element: <PodsDetail />,
  },
  {
    title: 'pod-detail',
    path: '/pods/:pods/namespace/:namespace/container/:container/detail',
    element: <ContainerDetail />,
  },
];

// 服务
const service = [
  {
    title: 'service',
    path: '/service',
    element: <Service />,
  },
  {
    title: 'service-detail', // 详情
    path: '/service/:service/namespace/:namespace/detail',
    element: <ServiceDetailsContainer />,
  },
  {
    title: 'service-pods-detail',
    path: '/service/:service/namespace/:namespace/detail/pods/:pods',
    element: <ServicePodsDetails />,
  },
  {
    title: 'service-pod-detail',
    path: '/service/:service/namespace/:namespace/detail/pods/:pods/:container',
    element: <ServiceContainerDetails />,
  },
  {
    title: 'service-create', // 创建
    /** 注意: 使用 react-router 的方法 generatePath 的时候可以保证完整路由字符串,方便维护 */
    path: '/service/create',
    element: <CreateService />,
  },
];

// 配置
const configuration = [
  {
    title: 'secrets',
    path: '/secrets',
    element: <Secret />,
  },
  {
    title: 'create-secret',
    path: '/secrets/create',
    element: <CreateSecret />,
  },
  {
    title: 'secret-detail',
    path: '/secrets/:secret/detail',
    element: <SecretDetail />,
  },
  {
    title: 'configmaps',
    path: '/configmaps',
    element: <Configmap />,
  },
  {
    title: 'create-configmap',
    path: '/configmaps/create',
    element: <CreateConfigmap />,
  },
  {
    title: 'configmap-detail',
    path: '/configmaps/:configmap/detail',
    element: <ConfigmapDetail />,
  },
];

// 存储
const storage = [
  {
    title: 'pvcs',
    path: '/pvcs',
    element: <Pvc />,
  },
  {
    title: 'create-pvc',
    path: '/pvcs/create',
    element: <CreatePvc />,
  },
  {
    title: 'pvc-detail',
    path: '/pvcs/:pvc/detail',
    element: <PvcDetail />,
  },
  {
    title: 'pvs',
    path: '/pvs',
    element: <Pv />,
  },
  {
    title: 'pv-detail',
    path: '/pvs/:pv/detail',
    element: <PvDetail />,
  },
  {
    title: 'storageclasses',
    path: '/storageclasses',
    element: <StorageClass />,
  },
  {
    title: 'create-storageclass',
    path: '/storageclasses/create',
    element: <CreateStorageClass />,
  },
  {
    title: 'storageclass-detail',
    path: '/storageclasses/:storageclass/detail',
    element: <StorageClassDetail />,
  },
];

const pathPrefixes = ['/cluster/:cluster/subcluster/:subcluster', '/wsp/:tenant/cluster/:cluster/subcluster/:subcluster'];

const leafRoutes = mergeRouters([clusterInfo, namespaces, workloads, containerGroup, service, storage, configuration]);

const clusterLayoutShell = {
  element: <LeftNavLayout />,
  errorElement: <ErrorBoundary />,
  children: leafRoutes,
};

export const clusterRouters = {
  path: '/',
  element: <AppLayout />,
  title: t('cluster:cluster-management'),
  children: joinPrefix([clusterLayoutShell], pathPrefixes),
};
